import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { FaArrowLeft, FaGem, FaEuroSign, FaChartLine, FaSearch, FaCheckCircle, FaEye } from 'react-icons/fa';
import { adminAPI } from '../../services/api';

const AdminPayments = () => {
  const { user } = useSelector((state) => state.auth);

  const [transactions, setTransactions] = useState([]);
  const [paymentStats, setPaymentStats] = useState({
    totalRevenue: 0,
    monthlyRevenue: 0,
    totalTransactions: 0,
    totalDiamantSold: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    fetchPaymentsData();
  }, []);

  const fetchPaymentsData = async () => {
    try {
      const response = await adminAPI.getPayments();
      const { data, stats } = response.data;
      setTransactions(data.data || []);
      setPaymentStats({
        totalRevenue: stats.totalRevenue,
        monthlyRevenue: stats.monthlyRevenue,
        totalTransactions: stats.totalTransactions,
        totalDiamantSold: stats.totalDiamantSold,
      });
    } catch (error) {
      console.error('Error fetching payments data:', error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredTransactions = transactions.filter(tx => {
    const u = tx.user || {};
    const matchesSearch =
      (u.username || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.first_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.last_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (tx.reference_id || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || tx.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const badges = {
      completed: 'badge bg-success',
      pending: 'badge bg-warning text-dark',
      failed: 'badge bg-danger',
      cancelled: 'badge bg-secondary',
    };
    return badges[status] || 'badge bg-secondary';
  };

  const getStatusText = (status) => {
    const texts = {
      completed: 'Complété',
      pending: 'En attente',
      failed: 'Échoué',
      cancelled: 'Annulé',
    };
    return texts[status] || status;
  };

  const getPriceCents = (tx) => {
    const cents = tx.metadata?.price_cents;
    if (!cents) return '—';
    return (cents / 100).toFixed(2) + ' ' + (tx.metadata?.currency || 'EUR');
  };

  if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="admin-payments min-vh-100" style={{ backgroundColor: 'var(--lova-bg-secondary)' }}>
      <div className="container-fluid py-4">
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-outline-secondary me-3"
                  onClick={() => window.location.href = '/admin/dashboard'}
                >
                  <FaArrowLeft className="me-2" />
                  Retour
                </button>
                <div>
                  <h1 className="h2 mb-1">Gestion des Paiements</h1>
                  <p className="mb-0" style={{ color: 'var(--lova-text-secondary)' }}>Recharges de Diamant effectuées</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-md-3 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaEuroSign className="text-success mb-2" size={24} />
                <h4 className="h5 mb-1">{paymentStats.totalRevenue.toFixed(2)} €</h4>
                <small style={{ color: 'var(--lova-text-secondary)' }}>Revenus Totaux</small>
              </div>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaChartLine className="text-primary mb-2" size={24} />
                <h4 className="h5 mb-1">{paymentStats.monthlyRevenue.toFixed(2)} €</h4>
                <small className="text-muted">Revenus ce Mois</small>
              </div>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaCheckCircle className="text-info mb-2" size={24} />
                <h4 className="h5 mb-1">{paymentStats.totalTransactions}</h4>
                <small className="text-muted">Transactions Totales</small>
              </div>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaGem className="text-warning mb-2" size={24} />
                <h4 className="h5 mb-1">{paymentStats.totalDiamantSold.toLocaleString()}</h4>
                <small className="text-muted">Diamants Vendus</small>
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-md-6">
            <div className="input-group">
              <span className="input-group-text"><FaSearch /></span>
              <input
                type="text"
                className="form-control"
                placeholder="Rechercher par utilisateur ou ID paiement..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">Tous les statuts</option>
              <option value="completed">Complété</option>
              <option value="pending">En attente</option>
              <option value="failed">Échoué</option>
              <option value="cancelled">Annulé</option>
            </select>
          </div>
          <div className="col-md-3">
            <div className="d-flex align-items-center h-100">
              <small className="text-muted">{filteredTransactions.length} transaction(s)</small>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th className="border-0 ps-4">Utilisateur</th>
                          <th className="border-0">Package</th>
                          <th className="border-0">Diamants</th>
                          <th className="border-0">Montant</th>
                          <th className="border-0">Statut</th>
                          <th className="border-0">ID Paiement</th>
                          <th className="border-0">Date</th>
                          <th className="border-0 pe-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTransactions.map((tx) => (
                          <tr key={tx.id}>
                            <td className="ps-4">
                              <div>
                                <strong>{tx.user?.first_name} {tx.user?.last_name}</strong>
                                <br />
                                <small className="text-muted">@{tx.user?.username}</small>
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-primary">
                                {tx.metadata?.package_name || '—'}
                              </span>
                            </td>
                            <td>
                              <strong className="text-warning">
                                <FaGem className="me-1" size={12} />
                                {tx.amount}
                              </strong>
                            </td>
                            <td>
                              <strong>{getPriceCents(tx)}</strong>
                            </td>
                            <td>
                              <span className={getStatusBadge(tx.status)}>
                                {getStatusText(tx.status)}
                              </span>
                            </td>
                            <td>
                              <small className="text-muted font-monospace">
                                {tx.reference_id || '—'}
                              </small>
                            </td>
                            <td>
                              <small className="text-muted">
                                {new Date(tx.created_at).toLocaleString('fr-FR')}
                              </small>
                            </td>
                            <td className="pe-4">
                              <button
                                className="btn btn-outline-primary btn-sm"
                                title="Voir détails"
                                onClick={() => {
                                  setSelectedTransaction(tx);
                                  setShowDetailsModal(true);
                                }}
                              >
                                <FaEye />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {filteredTransactions.length === 0 && (
                          <tr>
                            <td colSpan="8" className="text-center py-4 text-muted">
                              Aucune transaction trouvée
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showDetailsModal && selectedTransaction && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Détails de la Transaction</h5>
                <button type="button" className="btn-close" onClick={() => setShowDetailsModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6>Informations Utilisateur</h6>
                    <p><strong>Nom :</strong> {selectedTransaction.user?.first_name} {selectedTransaction.user?.last_name}</p>
                    <p><strong>Username :</strong> @{selectedTransaction.user?.username}</p>
                  </div>
                  <div className="col-md-6">
                    <h6>Détails du Paiement</h6>
                    <p><strong>Package :</strong> {selectedTransaction.metadata?.package_name || '—'}</p>
                    <p>
                      <strong>Diamants :</strong>{' '}
                      <span className="text-warning">
                        <FaGem className="me-1" size={12} />
                        {selectedTransaction.amount}
                      </span>
                    </p>
                    <p><strong>Montant payé :</strong> {getPriceCents(selectedTransaction)}</p>
                    <p>
                      <strong>Statut :</strong>{' '}
                      <span className={getStatusBadge(selectedTransaction.status)}>
                        {getStatusText(selectedTransaction.status)}
                      </span>
                    </p>
                    <p><strong>Date :</strong> {new Date(selectedTransaction.created_at).toLocaleString('fr-FR')}</p>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-12">
                    <h6>Informations Stripe</h6>
                    <p>
                      <strong>ID Paiement :</strong>{' '}
                      <small className="font-monospace">{selectedTransaction.reference_id || '—'}</small>
                    </p>
                    {selectedTransaction.processed_at && (
                      <p>
                        <strong>Traité le :</strong>{' '}
                        {new Date(selectedTransaction.processed_at).toLocaleString('fr-FR')}
                      </p>
                    )}
                    {selectedTransaction.description && (
                      <p><strong>Description :</strong> {selectedTransaction.description}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDetailsModal(false)}>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .min-vh-100 { min-height: 100vh; }
        .table th {
          font-weight: 600 !important;
          font-size: 0.875rem !important;
          text-transform: uppercase !important;
          letter-spacing: 0.5px !important;
          background: var(--lova-bg-secondary) !important;
          color: var(--lova-text-primary) !important;
          border-bottom: 2px solid var(--lova-gray-light) !important;
        }
        .table td {
          vertical-align: middle !important;
          background: var(--lova-bg-primary) !important;
          color: var(--lova-text-primary) !important;
        }
        .table tbody tr { background: var(--lova-bg-primary) !important; }
        .table tbody tr:hover { background: var(--lova-bg-secondary) !important; }
        .modal-content {
          background: var(--lova-bg-primary) !important;
          color: var(--lova-text-primary) !important;
        }
        .modal-header, .modal-footer { border-color: var(--lova-gray-light) !important; }
        .font-monospace { font-family: 'Courier New', monospace; font-size: 0.8rem; }
      `}</style>
    </div>
  );
};

export default AdminPayments;